import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { getUnitStays, listBuildingStaysUnits } from '../../../../api/stays-client';
import type { StaysBuildingUnit, StaysUnitState } from '../../../../interfaces/stays';
import { AppButton } from '../../../button/app-button';
import { StatusPill } from '../../../status-pill/status-pill';
import { ActionWithReason } from '../dashboard-shared-components/disabled-reason';
import { ListingBasicsModal } from '../dashboard-stays-components/listing-basics-modal';
import { openSetupAfterPromote } from '../../stays/setup/setup-steps';
import { ATTENTION_LABEL, ATTENTION_TONE, STATUS_LABEL, STATUS_TONE } from '../dashboard-stays-components/stays-labels';
import { Card, CardHeader, CardTitle, Muted } from './dashboard-units.styled';
import styled from '@emotion/styled';
import { palette, spacing, typography } from '../../../../style-utils/theme';

/**
 * LOBBERO STAYS ON THE UNIT SCREEN (ai-plans/stays plan S1, screen S-4).
 *
 *   - the header: "On Stays · Live" when the unit is listed; otherwise, for
 *     stays:promote, "Promote to Stays" — or the same button disabled with the
 *     reason in words (a resident lives here, the building's switch is off, the
 *     kind cannot be listed, the unit is offline);
 *   - a "Stays" card between the unit's details and its gallery.
 *
 * Nothing of this renders unless the organisation has Stays (the caller passes
 * `staysOn` from useStaysEntitlement) — and the unit's state is asked for only
 * then.
 */

export const useUnitStays = (
  unitId: number | undefined,
  enabled: boolean
): { state: StaysUnitState | null; reload: () => void } => {
  const [state, setState] = useState<StaysUnitState | null>(null);
  const seq = useRef(0);
  const load = useCallback(() => {
    if (!enabled || !unitId) {
      setState(null);

      return;
    }
    seq.current += 1;
    const mine = seq.current;
    getUnitStays(unitId)
      .then(s => {
        if (seq.current === mine) setState(s);
      })
      .catch(() => {
        // Fail closed: no Stays controls when the state could not be read.
        if (seq.current === mine) setState(null);
      });
  }, [unitId, enabled]);

  useEffect(load, [load]);

  return { state, reload: load };
};

/* Every unit of one building: listed? eligible? why not? (GET
   /stays/buildings/:id/units). Feeds the units list's row action. */
export const useBuildingStaysUnits = (
  buildingId: number | null | undefined,
  enabled: boolean
): { byUnit: Map<number, StaysBuildingUnit>; reload: () => void } => {
  const [byUnit, setByUnit] = useState<Map<number, StaysBuildingUnit>>(new Map());
  const seq = useRef(0);
  const load = useCallback(() => {
    if (!enabled || !buildingId) {
      setByUnit(new Map());

      return;
    }
    seq.current += 1;
    const mine = seq.current;
    listBuildingStaysUnits(buildingId)
      .then(r => {
        if (seq.current === mine) setByUnit(new Map(r.units.map(u => [u.unit_id, u])));
      })
      .catch(() => {
        if (seq.current === mine) setByUnit(new Map());
      });
  }, [buildingId, enabled]);

  useEffect(load, [load]);

  return { byUnit, reload: load };
};

/* The header control: the listing's pill, or Promote (enabled or explained). */
export const UnitStaysHeaderAction: React.FC<{
  state: StaysUnitState | null;
  canPromote: boolean;
  onPromote: () => void;
}> = ({ state, canPromote, onPromote }) => {
  if (!state) return null;
  if (state.listing) {
    return (
      <StatusPill tone={STATUS_TONE[state.listing.status]} dot data-testid="unit-stays-pill">
        {`On Stays · ${STATUS_LABEL[state.listing.status]}`}
      </StatusPill>
    );
  }
  if (!canPromote) return null;
  const reason = state.eligible ? null : (state.reasons[0] && state.reasons[0].message) || null;

  return (
    <ActionWithReason reason={reason}>
      {describedBy => (
        <AppButton variant="secondary" disabled={!state.eligible} aria-describedby={describedBy} onClick={onPromote}>
          Promote to Stays
        </AppButton>
      )}
    </ActionWithReason>
  );
};

const List = styled.ul`
  margin: 0;
  padding-left: ${spacing.md};
  font-family: ${typography.ui};
  font-size: ${typography.size.sm}px;
  line-height: ${typography.leading.normal};
  color: ${palette.slate};
`;

const Line = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: ${spacing.xs};
  min-width: 0;
`;

const Name = styled.span`
  font-family: ${typography.ui};
  font-size: ${typography.size.base}px;
  font-weight: ${typography.weight.semibold};
  color: ${palette.ink};
  overflow-wrap: anywhere;
`;

export const UnitStaysCard: React.FC<{ state: StaysUnitState | null }> = ({ state }) => {
  const navigate = useNavigate();
  if (!state) return null;
  const l = state.listing;

  return (
    <Card data-testid="unit-stays-card">
      <CardHeader>
        <CardTitle>Stays</CardTitle>
      </CardHeader>
      {l ? (
        <Line>
          <Name>{l.display_name}</Name>
          <StatusPill tone={STATUS_TONE[l.status]} dot>
            {STATUS_LABEL[l.status]}
          </StatusPill>
          {l.attention ? (
            <StatusPill tone={ATTENTION_TONE} dot>
              {ATTENTION_LABEL}
            </StatusPill>
          ) : null}
          <AppButton variant="ghost" onClick={() => navigate(`/dashboard/stays/${l.id}`)}>
            Open listing
          </AppButton>
        </Line>
      ) : state.eligible ? (
        <Muted>This unit can be listed for short stays.</Muted>
      ) : (
        <List>
          {state.reasons.map(r => (
            <li key={r.code}>{r.message}</li>
          ))}
        </List>
      )}
    </Card>
  );
};

/* The promote form for this unit, wired to its Stays state. Once promoted, the
   host goes straight into the setup guide at its next step, "House rules"
   (plan ISR, decision ISR-D5: the user, 2026-10-27, "yes"). */
export const UnitPromoteModal: React.FC<{
  open: boolean;
  state: StaysUnitState | null;
  onClose: () => void;
  onSaved: () => void;
}> = ({ open, state, onClose, onSaved }) => {
  const navigate = useNavigate();
  if (!state) return null;

  return (
    <ListingBasicsModal
      open={open}
      mode="promote"
      unit={{
        unit_id: state.unit_id,
        unit_number: state.unit_number,
        unit_name: state.unit_name,
        building_id: state.building_id,
        building_name: state.building_name,
        building_timezone: state.building_timezone,
        building_country: state.building_country,
      }}
      onClose={onClose}
      onSaved={listing => {
        onSaved();
        void openSetupAfterPromote(navigate, listing.id);
      }}
    />
  );
};
